import { ReactNode, useEffect, useState } from 'react'
import { supabase } from '../services/supabase'

interface ProtectedAdminRouteProps {
  children: ReactNode
  minimumRole?: 'moderator' | 'admin' | 'super_admin'
}

const ProtectedAdminRoute: React.FC<ProtectedAdminRouteProps> = ({ 
  children, 
  minimumRole = 'moderator' 
}) => {
  const [loading, setLoading] = useState(true)
  const [hasAccess, setHasAccess] = useState(false)

  useEffect(() => {
    checkAccess()
  }, [minimumRole])

  const checkAccess = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      if (!user) {
        window.location.href = '/login'
        return
      }

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single()

      if (error || !profile) {
        console.error('Error fetching user profile:', error)
        setHasAccess(false)
        setLoading(false)
        return
      }

      const roleHierarchy = {
        'player': 0,
        'moderator': 1,
        'admin': 2,
        'super_admin': 3
      }

      const userRoleLevel = roleHierarchy[profile.role as keyof typeof roleHierarchy] || 0
      const requiredRoleLevel = roleHierarchy[minimumRole]

      setHasAccess(userRoleLevel >= requiredRoleLevel)
    } catch (error) {
      console.error('Error checking admin access:', error)
      setHasAccess(false)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-white flex items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          Verifying admin access...
        </div>
      </div>
    )
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 text-xl mb-4">Access Denied</div>
          <div className="text-gray-400 mb-6">
            This page requires {minimumRole} privileges or higher.
          </div>
          <button
            onClick={() => window.location.href = '/dashboard'}
            className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

export default ProtectedAdminRoute